import * as React from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InsertPointProps {
  // Always-visible variant renders the pill without a hover state —
  // used for "Add block at end". Hover variant stays a thin line
  // between blocks until the user moves the pointer near it.
  variant?: 'hover' | 'always';
  label?: string;
  onClick: (rect: DOMRect) => void;
}

export function InsertPoint({ variant = 'hover', label, onClick }: InsertPointProps) {
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  function handleClick() {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) onClick(rect);
  }

  if (variant === 'always') {
    return (
      <div className="py-2 text-center">
        <button
          ref={buttonRef}
          type="button"
          onClick={handleClick}
          className={cn(
            'inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-dashed border-border bg-card px-4 py-2 text-sm font-medium text-text-secondary',
            'transition-colors hover:border-brand-500 hover:text-brand-700',
          )}
        >
          <Plus className="h-4 w-4" />
          {label ?? 'Add block'}
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group relative -my-1 flex h-4 items-center justify-center',
        'opacity-0 transition-opacity hover:opacity-100 focus-within:opacity-100',
      )}
    >
      <div className="absolute inset-x-12 top-1/2 h-px bg-border-subtle" aria-hidden />
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        aria-label={label ?? 'Insert block here'}
        className={cn(
          'relative inline-flex h-6 w-6 items-center justify-center rounded-full border border-brand-500 bg-card text-brand-700',
          'shadow-sm transition-transform hover:scale-110',
        )}
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
